import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { WalletUpcomingPayments } from '../../../../queries/pxtraCard';
import { cn } from '../../../util';
import { formatCents, formatCoverageCents, formatDate } from './format';
import { coverageTotal, UpcomingRow, upcomingRows } from './upcomingRows';

type Props = { upcoming: WalletUpcomingPayments };

const rowClass = (row: UpcomingRow) =>
  cn(
    'border-0 border-b border-l-2 border-solid border-b-grey-100',
    row.highlight === 'red' && 'border-l-error-main bg-error-lightest',
    row.highlight === 'green' && 'border-l-success-main bg-success-lightest',
    row.highlight === 'none' && 'border-l-transparent'
  );

const th = 'px-4 py-5 text-left text-xs font-medium text-grey-600';
const td = 'px-4 py-4';
const NO_DATE = '–';

export const UpcomingPaymentsTable: FC<Props> = ({ upcoming }) => {
  const { t } = useTranslation();
  const rows = upcomingRows(t, upcoming);

  return (
    <div className="w-0 min-w-full overflow-x-auto">
      <table
        className="w-full border-collapse text-base text-primary-main"
        data-testid="upcoming-payments-table"
      >
        <thead className="bg-grey-50">
          <tr className="border-0 border-l-2 border-solid border-transparent">
            <th className={th}>{t('pxtraCard:colKind')}</th>
            <th className={cn(th, 'text-right')}>{t('pxtraCard:colPersons')}</th>
            <th className={th}>{t('pxtraCard:colDueOn')}</th>
            <th className={th}>{t('pxtraCard:colCatchUpUntil')}</th>
            <th className={cn(th, 'text-right')}>{t('pxtraCard:colAmount')}</th>
            <th className={cn(th, 'text-right')}>{t('pxtraCard:colCoverage')}</th>
            <th className={th}>{t('pxtraCard:colStatus')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr className="border-0 border-b border-l-2 border-solid border-b-grey-100 border-l-transparent">
              <td colSpan={7} className="px-4 py-12 text-center">
                <p className="m-0 font-medium">{t('pxtraCard:upcomingEmptyTitle')}</p>
                <p className="m-0 mt-1 text-grey-500">{t('pxtraCard:upcomingEmptyText')}</p>
              </td>
            </tr>
          )}
          {rows.map((row) => (
            <tr
              key={row.key}
              className={rowClass(row)}
              data-highlight={row.highlight}
              data-testid={row.earlier ? 'upcoming-earlier-row' : undefined}
            >
              <td className={td}>{row.label}</td>
              <td className={cn(td, 'text-right')}>{row.persons}</td>
              <td className={td}>{row.dueOn ? formatDate(row.dueOn) : NO_DATE}</td>
              <td className={td}>{row.catchUpUntil ? formatDate(row.catchUpUntil) : NO_DATE}</td>
              <td className={cn(td, 'text-right')}>{formatCents(row.amountCents)}</td>
              <td className={cn(td, 'text-right', !row.covered && 'text-error-main')}>
                {formatCoverageCents(row.coverageCents)}
              </td>
              <td className={td}>
                <span className="inline-flex items-center gap-2 whitespace-nowrap text-base">
                  <span
                    className={cn(
                      'h-2 w-2 rounded-full',
                      row.covered ? 'bg-success-main' : 'bg-error-main'
                    )}
                  />
                  {row.covered ? t('pxtraCard:statusCovered') : t('pxtraCard:statusUncovered')}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="font-medium">
          <tr className="border-0 border-l-2 border-solid border-transparent">
            <td className={td}>{t('pxtraCard:total')}</td>
            <td className={cn(td, 'text-right')}>{upcoming.recipients_total}</td>
            <td colSpan={2} />
            <td className={cn(td, 'text-right')}>{formatCents(upcoming.total_cents)}</td>
            <td className={cn(td, 'text-right', coverageTotal(rows) < 0 && 'text-error-main')}>
              {formatCoverageCents(coverageTotal(rows))}
            </td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
};
